import { EventEmitter, Feature, InjectorService, TitlebarService } from '@tencent/workbench';
import { Events } from '@tencent/docs-titlebar-service';
import { DocumentStatus } from '@tencent/slide-sdk-model/src/manager/content-status-manager';
import BaseTitleBarFeature from '@framework/workbench/common/feature/base-titlebar-feature';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { onServicesReady } from '@src/slide/domain/slide-app/utils/service-awaiter';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

@Feature()
export class TitleBarFeature extends BaseTitleBarFeature {
    constructor(private eventEmitter: EventEmitter) {
        super();
    }

    public get isMasterEditType(): boolean {
        return this.getMasterLayoutEditService()?.isMasterEditType ?? false;
    }

    public get hasCGlobalPadId(): boolean {
        return !!SlideDataServices.DocInfoManager()?.cGlobalPadId;
    }

    public async initTitlebarExtension() {
        // 文档标题发生改变时通知更新
        SlideDataServices.SlideTitleManager.promise().then((slideTitleManager) => {
            slideTitleManager.onTitleChanged(this.handleTitleChanged);
        });
        eventBus.on(EventKeys.EDIT_TYPE_CHANGE, this.forceUpdate);
        // SlideDataServices.EventManager()?.on(EditEvents.ClientVarChanged, this.forceUpdate);
        // SlideDataServices.EventManager()?.on(EditEvents.SyncWorkerConnected, this.forceUpdate);

        this.localReadonly();
    }

    public async updateSaveTips() {
        SlideDataServices.SDKDataServices.EventManager.promise().then(({ revisionEvents }) => {
            revisionEvents.onCommitSucceed(() => {
                this.eventEmitter.emit(Events.UPDATE_SAVE_TIPS);
            });
        });

        // 本地编辑状态变化，同步titlebar更新显示文案
        if (ENV.IS_PC_LOCAL_EDIT) {
            onServicesReady([SlideDataServices.ContentStatusManager], (contentStatusManager) => {
                contentStatusManager.onDocumentStatusChange((status) => {
                    switch (status) {
                        case DocumentStatus.Modified:
                            this.eventEmitter.emit(Events.LOCAL_EDIT_CHANGED);
                            break;
                        case DocumentStatus.Syncing:
                            this.eventEmitter.emit(Events.LOCAL_FILE_SAVE_START);
                            break;
                        case DocumentStatus.Sync:
                            this.eventEmitter.emit(Events.LOCAL_FILE_SAVE_END);
                            // 保留原通知
                            this.eventEmitter.emit(Events.LOCAL_EDIT_SAVED);
                    }
                });
            });
        }
    }

    /**
     * 桌面端编辑密码选择只读查看时，需要通知titlebar-service更新标题
     * @private
     */
    private async localReadonly() {
        if (!ENV.IS_PC_LOCAL_EDIT) {
            return;
        }
        const readyPromise = new Promise((resolve) => {
            this.eventEmitter.listen(Events.BREADCRUMB_READY, () => setTimeout(() => resolve(null), 0));
        });
        eventBus.onIncludingPast(EventKeys.LOCAL_PASSWORD_READONLY, async () => {
            await readyPromise;
            this.eventEmitter.emit(Events.LOCAL_EDIT_READONLY_CHANGE, { readonly: true });
        });
    }

    private forceUpdate = () => {
        InjectorService.get(TitlebarService).$forceUpdate();
    };

    /**
     * 标题更新协同信息，通知顶部栏组件标题更新了
     */
    private handleTitleChanged = (title: string) => {
        this.eventEmitter.emit(Events.TITLE_CHANGE, title);
    };

    private getMasterLayoutEditService() {
        const appServices = AppServiceProvider.getInstance().getAppServices();
        if (browser.isMobile || appServices.getEntryType() !== AppEntryType.PC_EDIT) {
            return undefined;
        }
        return (appServices as IPcEditorAppServices).editableServiceLayer?.masterLayoutEditService;
    }
}
